import React from 'react';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import type { ResourcePermission, ResourceName } from '../../types/permissions';
import { getResourceName } from '../../services/permissionsApi';

interface PermissionSectionProps {
  resource: ResourceName;
  permissions: ResourcePermission;
  onChange: (resource: ResourceName, permissions: ResourcePermission) => void;
  disabled?: boolean;
  showManage?: boolean;
  showView?: boolean;
}

/**
 * PermissionSection Component
 * Displays and manages permissions for a single resource
 *
 * @param resource - Resource name (tasks, projects, etc.)
 * @param permissions - Current permissions for this resource
 * @param onChange - Callback when permissions change
 * @param disabled - Whether controls are disabled
 * @param showManage - Whether to show manage checkbox (default: true)
 * @param showView - Whether to show view checkbox (default: true)
 */
export const PermissionSection: React.FC<PermissionSectionProps> = ({
  resource,
  permissions,
  onChange,
  disabled = false,
  showManage = true,
  showView = true
}) => {
  const handleManageChange = (checked: boolean) => {
    // When manage is enabled, view must also be enabled
    const newPermissions: ResourcePermission = {
      manage: checked,
      view: checked ? true : permissions.view
    };
    onChange(resource, newPermissions);
  };

  const handleViewChange = (checked: boolean) => {
    // When view is disabled, manage must also be disabled
    const newPermissions: ResourcePermission = {
      manage: checked ? permissions.manage : false,
      view: checked
    };
    onChange(resource, newPermissions);
  };

  return (
    <div className="flex flex-col space-y-3 p-4 border border-gray-700 rounded-lg bg-gray-800/30">
      {/* Resource Name */}
      <div className="font-semibold text-white">
        {getResourceName(resource)}
      </div>

      {/* Permission Checkboxes */}
      <div className="flex flex-col space-y-2 pl-2">
        {showManage && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${resource}-manage`}
              checked={permissions.manage}
              onCheckedChange={handleManageChange}
              disabled={disabled}
              className="border-gray-600"
            />
            <Label
              htmlFor={`${resource}-manage`}
              className={`text-sm cursor-pointer ${
                disabled ? 'text-gray-500' : 'text-gray-300'
              }`}
            >
              Gerenciar (criar, editar, excluir)
            </Label>
          </div>
        )}

        {showView && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`${resource}-view`}
              checked={permissions.view}
              onCheckedChange={handleViewChange}
              disabled={disabled || permissions.manage} // Disabled if manage is true
              className="border-gray-600"
            />
            <Label
              htmlFor={`${resource}-view`}
              className={`text-sm cursor-pointer ${
                disabled || permissions.manage ? 'text-gray-500' : 'text-gray-300'
              }`}
            >
              Visualizar (somente leitura)
              {permissions.manage && (
                <span className="ml-2 text-xs text-blue-400">
                  (habilitado automaticamente)
                </span>
              )}
            </Label>
          </div>
        )}
      </div>

      {/* Help Text */}
      {!disabled && (
        <div className="text-xs text-gray-500 mt-2">
          {permissions.manage ? (
            <span>✓ Permissão total para este recurso</span>
          ) : permissions.view ? (
            <span>👁 Acesso somente leitura</span>
          ) : (
            <span>🚫 Sem acesso a este recurso</span>
          )}
        </div>
      )}
    </div>
  );
};

export default PermissionSection;
